import { Badge, Group, Stack, Text } from "@mantine/core"
import { DocumentActionsCell, Missing } from "@tfg/ui"
import { ReactNode } from "react"
import { useTranslation } from "react-i18next"
import { ZodObject } from "zod"

import {
  FindProductsProductStandardCertificateDto,
  findProductsProductStandardCertificateDtoCertificateRequestStatusEnum,
  findProductsProductStandardCertificateDtoStatusEnum,
} from "~/gen/api"
import { fetchDocumentUrl } from "~/hooks/useDocumentsQuery"
import { CertificateRequestLifecycleBadge } from "~/pages/Brand/Certificates/components/CertificateRequestLifecycleBadge"
import { getCertificateTemplate } from "~/pages/Brand/Certificates/components/templates"

type Props = {
  certificate: FindProductsProductStandardCertificateDto
  showDocumentIcons?: boolean
  maxListElements?: number
}

export const CertificateSummary = ({
  certificate,
  showDocumentIcons,
}: Props) => {
  const { t } = useTranslation(["certificates", "common"])
  const shouldShowDocumentActions =
    showDocumentIcons &&
    certificate.documentId &&
    certificate.certificateRequestStatus !==
      findProductsProductStandardCertificateDtoCertificateRequestStatusEnum.OPENED

  return (
    <Stack h="fit-content" w="100%">
      <Group justify="space-between">
        <CertificateRequestLifecycleBadge certificateRequest={certificate} />
        {shouldShowDocumentActions && (
          <DocumentActionsCell
            documentId={certificate.documentId}
            documentName={certificate.name}
            fetchDownloadUrl={() => fetchDocumentUrl(certificate.documentId)}
            fetchViewUrl={() => fetchDocumentUrl(certificate.documentId)}
            missingDocumentText={t("common:documentUpload.missingDocument")}
          />
        )}
      </Group>
      <Group w="100%" justify="space-between" wrap="nowrap">
        <InfoItem
          label={t("certificates:modal.labels.name")}
          value={<TextValue value={certificate.name} />}
        />
      </Group>

      {certificate.status !==
        findProductsProductStandardCertificateDtoStatusEnum.NOT_UPLOADED && (
        <CertificateInfo certificate={certificate} />
      )}
    </Stack>
  )
}

export function CertificateInfo({
  certificate,
}: {
  certificate: FindProductsProductStandardCertificateDto
}) {
  const { t } = useTranslation("certificates")

  const template = getCertificateTemplate(certificate.type.category)

  const formatter = new Intl.DateTimeFormat("en-US", { dateStyle: "short" })

  return (
    <Stack>
      <Group wrap="nowrap">
        {certificate.type && (
          <InfoItem
            label={t("modal.labels.type")}
            value={<TextValue value={certificate.type.name} />}
          />
        )}
      </Group>
      <Group wrap="nowrap">
        <InfoItem
          label={t("modal.labels.issueDate")}
          value={
            certificate.issueDate && (
              <TextValue
                value={formatter.format(new Date(certificate.issueDate))}
              />
            )
          }
        />
        {"expiryDate" in template.shape && (
          <InfoItem
            label={t("modal.labels.expiryDate")}
            value={
              certificate.expiryDate && (
                <TextValue
                  value={formatter.format(new Date(certificate.expiryDate))}
                />
              )
            }
          />
        )}
      </Group>
      <InfoItem
        label={t("modal.labels.reference")}
        value={<TextValue value={certificate.reference} />}
      />
      {"score" in certificate && (
        <InfoItem
          label={t("modal.labels.score")}
          value={<TextValue value={certificate.score} />}
        />
      )}
      <LongSummaryFields
        certificate={certificate}
        templateShape={template.shape}
      />
    </Stack>
  )
}

type InfoItemProps = {
  label: string
  value: ReactNode | null
  w?: string
}

export const InfoItem = ({ label, value, ...otherProps }: InfoItemProps) => {
  return (
    <Stack gap="5px" {...otherProps}>
      <Text miw={150}>{label}</Text>
      {value ? value : <Missing />}
    </Stack>
  )
}

type LongSummaryFieldsProps = {
  certificate: FindProductsProductStandardCertificateDto
  templateShape: ZodObject<any>
}

function LongSummaryFields({
  certificate,
  templateShape,
}: LongSummaryFieldsProps) {
  const { t } = useTranslation("certificates")

  return (
    <Stack>
      {"certificationBody" in certificate && (
        <InfoItem
          label={t("modal.labels.certificationBody")}
          value={<TextValue value={certificate.certificationBody} />}
        />
      )}
      {"certifiedOrganizationLicenseNumber" in templateShape && (
        <InfoItem
          label={t("modal.labels.certifiedOrganizationLicenseNumber")}
          value={
            <TextValue value={certificate.certifiedOrganizationLicenseNumber} />
          }
        />
      )}
      {"certifiedOrganizationName" in templateShape && (
        <InfoItem
          label={t("modal.labels.certifiedOrganizationName")}
          value={<TextValue value={certificate.certifiedOrganizationName} />}
        />
      )}
      {"certifiedOrganizationAddress" in templateShape && (
        <InfoItem
          label={t("modal.labels.certifiedOrganizationAddress")}
          value={<TextValue value={certificate.certifiedOrganizationAddress} />}
        />
      )}
      {"productCategories" in templateShape && (
        <InfoItem
          label={t("modal.labels.productCategories")}
          value={
            <TextValue
              value={certificate.productCategories}
              maxListElements={5}
            />
          }
        />
      )}
      {"processingSteps" in templateShape && (
        <InfoItem
          label={t("modal.labels.processingSteps")}
          value={
            <TextValue
              value={certificate.processingSteps}
              maxListElements={5}
            />
          }
        />
      )}
      {"productDetails" in templateShape && (
        <InfoItem
          label={t("modal.labels.productDetails")}
          value={
            <TextValue value={certificate.productDetails} maxListElements={5} />
          }
        />
      )}
      {"buyerOfCertifiedProducts" in templateShape && (
        <InfoItem
          label={t("modal.labels.buyerOfCertifiedProducts")}
          value={<TextValue value={certificate.buyerOfCertifiedProducts} />}
        />
      )}
      {"certifiedWeight" in templateShape && (
        <InfoItem
          label={t("modal.labels.certifiedWeight")}
          value={<TextValue value={certificate.certifiedWeight} />}
        />
      )}
      {"linkedScopeCertificateNumber" in templateShape && (
        <InfoItem
          label={t("modal.labels.linkedScopeCertificateNumber")}
          value={<TextValue value={certificate.linkedScopeCertificateNumber} />}
        />
      )}
    </Stack>
  )
}

export function TextValue({
  value,
  maxListElements,
}: {
  value: string | null | undefined | string[]
  maxListElements?: number
}) {
  if (!value) {
    return <Missing />
  }

  if (Array.isArray(value)) {
    const initialLength = value.length
    if (maxListElements) value = value.slice(0, maxListElements)

    if (!initialLength) {
      return <Missing />
    }

    return (
      <Group gap="xs">
        {value.map((v, index) => (
          <Badge size="xs" key={index}>
            {v}
          </Badge>
        ))}
        {maxListElements && initialLength > maxListElements && (
          <Badge size="xs">...</Badge>
        )}
      </Group>
    )
  }

  return <Text fw="bold">{value}</Text>
}
